import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";
import SliderFormModal from "./components/SliderFormModal";

function SliderManagement() {
  const [sliders, setSliders] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sliderToDelete, setSliderToDelete] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [sliderData, setSliderData] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    image_en: null,
    image_fr: null,
    image_en_preview: null,
    image_fr_preview: null,
    status: "active",
  });

  const initialState = {
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
    image_en: null,
    image_fr: null,
    image_en_preview: null,
    image_fr_preview: null,
    status: "active",
  };
  const [accessToken, setAccessToken] = useState("");

  // Fetch sliders with pagination
  const fetchSliders = useCallback(
    async (page, searchValue) => {
      setLoading(true);
      try {
        const params = { page, total: totalItems, search: searchValue };
        const headers = accessToken ? { "x-access-token": accessToken } : {};
        const response = await apiRequest.get(endPoints.GET_SLIDERS,
          params,
          headers,
        );

        if (response.success) {
          const { items, pagination } = response.data;
          setSliders(items);
          setTotalPages(pagination.total_pages);
          setCurrentPage(pagination.current_page);
          setTotalItems(pagination.totalItems);
        }
      } catch (error) {
        console.error("Error fetching sliders:", error);
        setError("Failed to fetch sliders. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [accessToken, totalItems]
  );

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setAccessToken(token || "");
  }, []);

  useEffect(() => {
    fetchSliders(currentPage, searchValue);
  }, [currentPage, fetchSliders, searchValue]);


  // Handle modal open/close
  const handleOpenModal = () => {
    setOpenModal(true);
    setSliderData(initialState);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setSliderData(initialState);
    setIsEditing(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSliderData({ ...sliderData, [name]: value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Append text fields
      formData.append('name_en', sliderData.name_en);
      formData.append('name_fr', sliderData.name_fr);
      formData.append('description_en', sliderData.description_en);
      formData.append('description_fr', sliderData.description_fr);

      // Append image files
      if (sliderData.image_en instanceof File) {
        formData.append('image_en', sliderData.image_en);
      }

      if (sliderData.image_fr instanceof File) {
        formData.append('image_fr', sliderData.image_fr);
      }


      // Determine the endpoint and method based on whether it's an edit or add operation
      const endPoint = isEditing
        ? `${endPoints.UPDATE_SLIDER}/${currentId}`
        : `${endPoints.ADD_SLIDER}`;
      const requestMethod = isEditing ? apiRequest.put : apiRequest.post;

      // Send the request
      const response = await requestMethod(endPoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle the response
      if (response.success) {
        setSuccess(isEditing ? "Slider updated successfully." : "Slider added successfully.");
        fetchSliders(currentPage); // Refresh the slider list
        handleCloseModal(); // Close the modal
      } else {
        setError(response.message || 'Failed to add/update slider.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing your request.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit slider
  const handleEdit = (slider) => {
    handleOpenModal();
    setIsEditing(true);
    setSliderData({
      name_en: slider.name_en || "",
      name_fr: slider.name_fr || "",
      description_en: slider.description_en || "",
      description_fr: slider.description_fr || "",
      image_en: slider.image_en || null,
      image_fr: slider.image_fr || null,
      image_en_preview: slider.image_en ? `${apiRequest.BASE_URL}/${slider.image_en}` : null,
      image_fr_preview: slider.image_fr ? `${apiRequest.BASE_URL}/${slider.image_fr}` : null,
      status: slider.status || "active",
    });

    setCurrentId(slider.id);
  };

  // Handle delete slider
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Send the DELETE request to the API
      const response = await apiRequest.delete(
        `${endPoints.DELETE_SLIDER}/${id}`,
        {
          headers: {
          },
        }
      );

      // Handle the response
      if (response.success) {
        setSuccess("Slider deleted successfully.");

        // Update the local state to remove the deleted slider
        setSliders((prev) => prev.filter((slider) => slider.id !== id));
      } else {
        setError(response.message || "Failed to delete the slider.");
      }
    } catch (error) {
      console.error("Error deleting slider:", error);
      setError("An error occurred while deleting the slider.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage); // Update the current page
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // Update the state with the file and its preview
      setSliderData((prevState) => ({
        ...prevState,
        [fieldName]: file, // Store the file object
        [`${fieldName}_preview`]: URL.createObjectURL(file), // Generate a preview URL
      }));
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const payload = {
        status: newStatus,
      };

      const headers = {
        'Content-Type': 'application/json',
      };

      // Send the PUT request to update the slider status
      const response = await apiRequest.put(
        `${endPoints.STATUS_SLIDER}/${id}`,
        payload,
        { headers }
      );

      // Handle the response
      if (response.success) {
        setSuccess("Slider status updated successfully.");

        // Update the local state immediately
        setSliders((prevSliders) =>
          prevSliders.map((slider) =>
            slider.id === id ? { ...slider, status: newStatus } : slider
          )
        );
      } else {
        setError(response.message || 'Failed to update slider status.');
      }
    } catch (error) {
      console.error('Error updating slider status:', error);
      setError('An error occurred while updating the slider status.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Sliders
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                >
                  <TextField
                    label="Search"
                    variant="outlined"
                    margin="normal"
                    placeholder="Search by title or description"
                    style={{ width: "300px" }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{
                      whiteSpace: "nowrap",
                      color: "white",
                    }}
                  >
                    Add Slider
                  </Button>
                </MDBox>
                <TableContainer component={Paper} style={{ marginTop: "1px" }}>
                  <Table>
                    {/* <TableHead> */}
                    <TableRow>
                      <TableCell style={{ fontWeight: "bold" }}>Title</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>Photo</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell style={{ fontWeight: "bold" }}>Actions</TableCell>
                    </TableRow>
                    {/* </TableHead> */}
                    <TableBody>
                      {sliders.map((slider) => (
                        <TableRow key={slider.id}>
                          <TableCell>{slider.name_en + ' / ' + slider.name_fr}</TableCell>
                          <TableCell>
                            <img
                              src={`${apiRequest.BASE_URL}/${slider.image_en}`}
                              alt={slider.name_en}
                              style={{ width: "100px", height: "auto" }}
                            />
                            /
                            <img
                              src={`${apiRequest.BASE_URL}/${slider.image_fr}`}
                              alt={slider.name_fr}
                              style={{ width: "100px", height: "auto" }}
                            />
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={slider.status === "active"} // Assuming "active" is the active status
                              onChange={() => handleStatusToggle(slider.id, slider.status)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={() => handleEdit(slider)}
                              color="primary"
                              size="small"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => {
                                setSliderToDelete(slider.id); // Set the slider ID to delete
                                setDeleteDialogOpen(true); // Open the confirmation dialog
                              }}
                              color="error"
                              size="small"
                              style={{ marginLeft: "8px" }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages} {/* Display current page and total pages */}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal for Adding/Editing Slider */}
      <SliderFormModal
        open={openModal}
        isEditing={isEditing}
        sliderData={sliderData}
        onClose={handleCloseModal}
        onInputChange={handleInputChange}
        onFileChange={handleFileChange}
        onSubmit={handleSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Slider</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this slider? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false); // Close the dialog
              handleDelete(sliderToDelete); // Proceed with deletion
            }}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Positioning the Snackbar
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default SliderManagement;
